import React, { ReactElement } from "react"
import { Link } from "gatsby"
import Layout from "../../components/layout"
import InlineCode from "../../components/inlineCode"
import Examples from "../../components/codeExamples"
import { ValidatorFunction, CodeExample } from "../../components/validatorFuncs"

const gtExample = `var validator = new ValidatorBuilder<int>()
    .GreaterThan(18)
    .Build();

var result = validator.Validate(16);`

const gtSelectorExample = `var validator = new ValidatorBuilder<Employee>()
    .GreaterThan(e => e.Age, 18)
    .Build();

var result = validator.Validate(new Employee { Age = 20 });`

const gtForExample = `var validator = new ValidatorBuilder<Employee>()
    .For(e => e.Age, v => v.GreaterThan(18))
    .Build();

var result = validator.Validate(new Employee { Age = 17 });`

const gteqExample = `var validator = new ValidatorBuilder<int>()
    .GreaterThanOrEqual(18)
    .Build();
  
var result = validator.Validate(16);`

const gteqSelectorExample = `var validator = new ValidatorBuilder<Employee>()
    .GreaterThanOrEqual(e => e.Age, 18)
    .Build();

var result = validator.Validate(new Employee { Age = 20 });`

const gteqForExample = `var validator = new ValidatorBuilder<Employee>()
    .For(e => e.Age, v => v.GreaterThanOrEqual(18))
    .Build();

var result = validator.Validate(new Employee { Age = 17 });`

const compExample = `var validator = new ValidatorBuilder<LoginModel>()
    .Compare(m => m.Password, m => m.PasswordConfirm)
    .Build();

var result = validator.Validate(new LoginModel 
{ 
    Password = "StrongP@ssw0rd",
    PasswordConfirm = "StrongPassw0rd"
});`

const compGtExample = `var validator = new ValidatorBuilder<TwoNumbersModel>()
    .CompareGreaterThan(m => m.Number1, m => m.Number2)
    .Build();

var result = validator.Validate(new TwoNumbersModel 
{ 
    Number1 = 8,
    Number2 = 7
});`

const compGtEqExample = `var validator = new ValidatorBuilder<TwoNumbersModel>()
    .CompareGreaterThanOrEqual(m => m.Number1, m => m.Number2)
    .Build();

var result = validator.Validate(new TwoNumbersModel 
{ 
    Number1 = 8,
    Number2 = 7
});`

const compLtExample = `var validator = new ValidatorBuilder<TwoNumbersModel>()
    .CompareLessThan(m => m.Number1, m => m.Number2)
    .Build();

var result = validator.Validate(new TwoNumbersModel 
{ 
    Number1 = 7,
    Number2 = 8
});`

const compLtEqExample = `var validator = new ValidatorBuilder<TwoNumbersModel>()
    .CompareLessThanOrEqual(m => m.Number1, m => m.Number2)
    .Build();

var result = validator.Validate(new TwoNumbersModel 
{ 
    Number1 = 7,
    Number2 = 8
});`

function ComparisonFunctions(): ReactElement {
  return (
    <Layout
      title="Comparison functions"
      description="Validation functions for use on IComparable types"
    >
      <h1>Comparison Functions</h1>
      <p>
        These functions can be used on <InlineCode>IComparable</InlineCode>{" "}
        types.
      </p>
      <p>
        <ul className="list">
          {[
            "GreaterThan",
            "GreaterThanOrEqual",
            "LessThan",
            "LessThanOrEqual",
            "Range",
            "Compare",
            "CompareGreaterThan",
            "CompareGreaterThanOrEqual",
            "CompareLessThan",
            "CompareLessThanOrEqual",
          ].map((func) => (
            <li>
              <Link to={`/functions/comparison/#${func}`}>{func}</Link>
            </li>
          ))}
        </ul>
      </p>
      <hr className="my-6" />
      <ValidatorFunction
        name="GreaterThan"
        description="Ensures the value being validated is greater than a specified value."
        methods={[
          `GreaterThan<T>(T other)`,
          `GreaterThan<T, P>(Expression<Func<T, P>> selector, P other)`,
        ]}
        brokenRule="Value must be greater than '{other}'."
      >
        <CodeExample label="Using type instance." code={gtExample} />
        <CodeExample
          label="Using selector expression."
          code={gtSelectorExample}
        />
        <CodeExample
          label={
            <>
              Using <InlineCode>For</InlineCode> function.
            </>
          }
          code={gtForExample}
        />
      </ValidatorFunction>
      <hr className="my-6" />
      <ValidatorFunction
        name="GreaterThanOrEqual"
        description="Ensures the value being validated is greater than or equal to a specified value."
        methods={[
          `GreaterThanOrEqual<T>(T other)`,
          `GreaterThanOrEqual<T, P>(Expression<Func<T, P>> selector, P other)`,
        ]}
        brokenRule="Value must be greater than or equal to '{other}'."
      >
        <CodeExample label="Using type instance." code={gteqExample} />
        <CodeExample
          label="Using selector expression."
          code={gteqSelectorExample}
        />
        <CodeExample
          label={
            <>
              Using <InlineCode>For</InlineCode> function.
            </>
          }
          code={gteqForExample}
        />
      </ValidatorFunction>
      <hr className="my-6" />
      <ValidatorFunction
        name="LessThan"
        description="Ensures the value being validated is less than a specified value."
        methods={[
          `LessThan<T>(T other)`,
          `LessThan<T, P>(Expression<Func<T, P>> selector, P other)`,
        ]}
        brokenRule="Value must be less than '{other}'."
      >
        <CodeExample
          label="Using type instance."
          code={Examples.Comparison.LessThan.Example1}
        />
        <CodeExample
          label="Using selector expression."
          code={Examples.Comparison.LessThan.Example2}
        />
        <CodeExample
          label={
            <>
              Using <InlineCode>For</InlineCode> function.
            </>
          }
          code={Examples.Comparison.LessThan.Example3}
        />
      </ValidatorFunction>
      <hr className="my-6" />
      <ValidatorFunction
        name="LessThanOrEqual"
        description="Ensures the value being validated is less than or equal to a specified value."
        methods={[
          `LessThanOrEqual<T>(T other)`,
          `LessThanOrEqual<T, P>(Expression<Func<T, P>> selector, P other)`,
        ]}
        brokenRule="Value must be less than or equal to '{other}'."
      >
        <CodeExample
          label="Using type instance."
          code={Examples.Comparison.LessThanOrEqual.Example1}
        />
        <CodeExample
          label="Using selector expression."
          code={Examples.Comparison.LessThanOrEqual.Example2}
        />
        <CodeExample
          label={
            <>
              Using <InlineCode>For</InlineCode> function.
            </>
          }
          code={Examples.Comparison.LessThanOrEqual.Example3}
        />
      </ValidatorFunction>
      <hr className="my-6" />
      <ValidatorFunction
        name="Range"
        description="Ensures the value being validated is within (inclusive) a specified value."
        methods={[
          `Range<T>(T lower, T upper)`,
          `Range<T, P>(Expression<Func<T, P>> selector, P lower, P upper)`,
        ]}
        brokenRule="Value must be in range '{lower}' to '{upper}'."
      >
        <CodeExample
          label="Using type instance."
          code={Examples.Comparison.Range.Example1}
        />
        <CodeExample
          label="Using selector expression."
          code={Examples.Comparison.Range.Example2}
        />
        <CodeExample
          label={
            <>
              Using <InlineCode>For</InlineCode> function.
            </>
          }
          code={Examples.Comparison.Range.Example3}
        />
      </ValidatorFunction>
      <hr className="my-6" />
      <ValidatorFunction
        name="Compare"
        description="Ensures two resolved values from the target of selector expressions are equal."
        methods={[
          <span>
            Compare&lt;T, P&gt;(
            <br />
            <span className="ml-4">
              Expression&lt;Func&lt;T, P&gt;&gt; leftSelector,
            </span>
            <br />
            <span className="ml-4">
              Expression&lt;Func&lt;T, P&gt;&gt; rightSelector
            </span>
            <br />)
          </span>,
        ]}
        brokenRule="Value must be equal to value of {rightSelector}."
      >
        <CodeExample label="Using selector expressions." code={compExample} />
      </ValidatorFunction>
      <hr className="my-6" />
      <ValidatorFunction
        name="CompareGreaterThan"
        description="Ensures the value of the target of the first selector expression is greater than the value of the target of the second selector expression."
        methods={[
          <span>
            CompareGreaterThan&lt;T, P&gt;(
            <br />
            <span className="ml-4">
              Expression&lt;Func&lt;T, P&gt;&gt; leftSelector,
            </span>
            <br />
            <span className="ml-4">
              Expression&lt;Func&lt;T, P&gt;&gt; rightSelector
            </span>
            <br />)
          </span>,
        ]}
        brokenRule="Value must be greater than value of {rightSelector}."
      >
        <CodeExample label="Using selector expressions." code={compGtExample} />
      </ValidatorFunction>
      <hr className="my-6" />
      <ValidatorFunction
        name="CompareGreaterThanOrEqual"
        description="Ensures the value of the target of the first selector expression is greater than or equal to the value of the target of the second selector expression."
        methods={[
          <span>
            CompareGreaterThanOrEqual&lt;T, P&gt;(
            <br />
            <span className="ml-4">
              Expression&lt;Func&lt;T, P&gt;&gt; leftSelector,
            </span>
            <br />
            <span className="ml-4">
              Expression&lt;Func&lt;T, P&gt;&gt; rightSelector
            </span>
            <br />)
          </span>,
        ]}
        brokenRule="Value must be greater than or equal to value of {rightSelector}."
      >
        <CodeExample
          label="Using selector expressions."
          code={compGtEqExample}
        />
      </ValidatorFunction>
      <hr className="my-6" />
      <ValidatorFunction
        name="CompareLessThan"
        description="Ensures the value of the target of the first selector expression is less than the value of the target of the second selector expression."
        methods={[
          <span>
            CompareLessThan&lt;T, P&gt;(
            <br />
            <span className="ml-4">
              Expression&lt;Func&lt;T, P&gt;&gt; leftSelector,
            </span>
            <br />
            <span className="ml-4">
              Expression&lt;Func&lt;T, P&gt;&gt; rightSelector
            </span>
            <br />)
          </span>,
        ]}
        brokenRule="Value must be less than value of {rightSelector}."
      >
        <CodeExample label="Using selector expressions." code={compLtExample} />
      </ValidatorFunction>
      <hr className="my-6" />
      <ValidatorFunction
        name="CompareLessThanOrEqual"
        description="Ensures the value of the target of the first selector expression is less than or equal to the value of the target of the second selector expression."
        methods={[
          <span>
            CompareLessThanOrEqual&lt;T, P&gt;(
            <br />
            <span className="ml-4">
              Expression&lt;Func&lt;T, P&gt;&gt; leftSelector,
            </span>
            <br />
            <span className="ml-4">
              Expression&lt;Func&lt;T, P&gt;&gt; rightSelector
            </span>
            <br />)
          </span>,
        ]}
        brokenRule="Value must be less than or equal to value of {rightSelector}."
      >
        <CodeExample
          label="Using selector expressions."
          code={compLtEqExample}
        />
      </ValidatorFunction>
    </Layout>
  )
}

export default ComparisonFunctions
